import React from 'react';
import { SpeakerStats } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';

interface TalkPercentSummaryCardProps {
  speakerStats?: SpeakerStats;
}

const TalkPercentSummaryCard: React.FC<TalkPercentSummaryCardProps> = ({
  speakerStats,
}) => {
  if (!speakerStats) {
    return (
      <SummarySectionCard
        title="Talk %"
        icon={<span>&#128172;</span>}
        resultContent={null}
        missingDataMessage="Speaker stats not available for this session."
      />
    );
  }

  const repPct = Math.round(speakerStats.rep_pct ?? 0);
  const customerPct = Math.round(speakerStats.customer_pct ?? 0);

  const barColor =
    repPct > 65 ? 'bg-red-500' : repPct > 50 ? 'bg-yellow-500' : 'bg-green-500';

  const badgeColor =
    repPct > 65
      ? 'bg-red-100 text-red-700 border border-red-200'
      : repPct > 50
      ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
      : 'bg-green-100 text-green-700 border border-green-200';

  const statusBadge = (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>Rep: {repPct}%</span>
  );

  const coachingText =
    repPct > 65
      ? `Talk time was high at ${repPct}%. Aim to stay under 50% to give the customer more space to share their needs and concerns.`
      : repPct > 50
      ? `Talk ratio of ${repPct}% is slightly above ideal. Asking more open-ended questions can help balance the conversation.`
      : `Good balance at ${repPct}% talk time. Keep encouraging the customer to share and stay curious.`;

  const resultContent = (
    <div className="space-y-2">
      <div className="flex text-xs text-gray-500 justify-between mb-1">
        <span>Rep ({repPct}%)</span>
        <span>Customer ({customerPct}%)</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${repPct}%` }}
        />
        <div
          className="h-full bg-blue-400 transition-all duration-500"
          style={{ width: `${customerPct}%` }}
        />
      </div>
      <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-1">
        <span className="flex items-center gap-1"><span className={`inline-block w-2 h-2 rounded-full ${barColor}`} /> Rep</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-blue-400" /> Customer</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2 text-center">
        <div className="bg-gray-50 rounded p-2">
          <p className="text-xs font-bold text-gray-700">{speakerStats.rep_word_count?.toLocaleString() ?? '—'}</p>
          <p className="text-[10px] text-gray-400">Rep words</p>
        </div>
        <div className="bg-gray-50 rounded p-2">
          <p className="text-xs font-bold text-gray-700">{speakerStats.customer_word_count?.toLocaleString() ?? '—'}</p>
          <p className="text-[10px] text-gray-400">Customer words</p>
        </div>
      </div>
    </div>
  );

  return (
    <SummarySectionCard
      title="Talk %"
      icon={<span>&#128172;</span>}
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={[coachingText]}
      sectionKey="talk_pct"
    />
  );
};

export default TalkPercentSummaryCard;
